import { useFormik } from "formik";
// GMA-1228: `moment-timezone` so `initData` can render the persisted ISO in
// the client's wall-clock time (caregivers in a different zone used to see
// shifted times in the edit form and round-trip-save the skew).
import moment from "moment-timezone";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useActivities } from "../../../state/ActivityContext";
import { ClientContext } from "../../../state/ClientContext";
import { addBreadcrumb, captureMessage } from "../../../config/sentry";
import {
  buildZonedInstant,
  extractZonedFormFields,
  MESSAGE_ACTIVITY_NO_PAST_START,
  MESSAGE_ACTIVITY_NO_PAST_START_TIME,
  resolveActivityTimezone,
  toWallClockHHmm,
  yupMixedTimeToWallHHmm,
} from "../../../utils/activityTimezone";

const SUPPORTED_YOUTUBE_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
];
const YT_ID_PATTERN = /^[\w-]{11}$/;

function isYouTubeHost(hostname) {
  return (
    SUPPORTED_YOUTUBE_HOSTS.includes(hostname) ||
    hostname.endsWith(".youtube.com")
  );
}

/**
 * Mirrors iOS VideoPlayerView.swift `extractYouTubeID(from:)` (lines 270-292).
 * Returns: true = supported YouTube, false = unsupported YouTube, null = not YouTube.
 */
export function isIOSSupportedYouTubeUrl(url) {
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.toLowerCase();
    if (!isYouTubeHost(host)) return null;
    if (!SUPPORTED_YOUTUBE_HOSTS.includes(host)) return false;
    if (host === "youtu.be") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return !!(id && YT_ID_PATTERN.test(id));
    }
    if (parsed.pathname.startsWith("/embed/")) {
      const id = parsed.pathname.split("/").filter(Boolean).pop();
      return !!(id && YT_ID_PATTERN.test(id));
    }
    const v = new URLSearchParams(parsed.search).get("v");
    return !!(v && YT_ID_PATTERN.test(v));
  } catch {
    return null;
  }
}

/** @deprecated Prefer `MESSAGE_ACTIVITY_NO_PAST_START` from `utils/activityTimezone`. */
export const MESSAGE_ACTIVITY_EDIT_NO_PAST_START = MESSAGE_ACTIVITY_NO_PAST_START;

/** @deprecated Prefer `MESSAGE_ACTIVITY_NO_PAST_START_TIME` from `utils/activityTimezone`. */
export const MESSAGE_ACTIVITY_EDIT_NO_PAST_START_TIME =
  MESSAGE_ACTIVITY_NO_PAST_START_TIME;

const buildValidationSchema = ({
  isEditingActivity,
  rawClientTimezone,
  clientId,
}) =>
  yup.object({
    // GMA-1228 follow-up (web parity with calendar range): edits that leave a
    // past start day let the PATCH succeed while the portal month/grid query
    // drops the occurrence — caregivers think the save vanished. Blocking
    // past start days on edit aligns with visible calendar range.
    date_from: yup
      .string()
      .required("Select start date.")
      .test({
        name: "no-past-start-day",
        message: MESSAGE_ACTIVITY_NO_PAST_START,
        test(val) {
          if (!val) return true;
          const { zone } = resolveActivityTimezone(rawClientTimezone, {
            source: "validate-wall-date",
            clientId,
            mode: isEditingActivity ? "edit" : "create",
          });
          const startDay = moment.tz(val, "YYYY-MM-DD", zone).startOf("day");
          const todayLocal = moment.tz(zone).startOf("day");
          return (
            startDay.isSame(todayLocal, "day") || startDay.isAfter(todayLocal)
          );
        },
      }),
    title: yup
      .string()
      .required("Enter activity name.")
      .max(100, "Activity names can't be over 100 characters."),
    start_time: yup
      .mixed()
      .transform((val) => yupMixedTimeToWallHHmm(val))
      // GMA-1234: Time fields are not user-facing for all-day rows. The
      // payload builder synthesises midnight ↔ midnight ISO instants from
      // date_from / date_to (see ActivityContext.{create,update}Activity).
      .when("is_all_day", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.required("Select start date and time.").test({
            name: "no-past-start-instant-on-today",
            message: MESSAGE_ACTIVITY_NO_PAST_START_TIME,
            test(val) {
              if (!val) return true;
              const dateFrom = this.parent.date_from;
              if (!dateFrom) return true;
              const { zone } = resolveActivityTimezone(rawClientTimezone, {
                source: "validate-start-instant",
                clientId,
                mode: isEditingActivity ? "edit" : "create",
              });
              const startDay = moment
                .tz(dateFrom, "YYYY-MM-DD", zone)
                .startOf("day");
              const todayLocal = moment.tz(zone).startOf("day");
              if (!startDay.isSame(todayLocal, "day")) return true;

              const startInstant = buildZonedInstant(dateFrom, val, zone, {
                source: "validate-start-instant",
                clientId,
              });
              if (!startInstant.isValid()) return true;
              const now = moment.tz(zone);
              return startInstant.valueOf() >= now.valueOf();
            },
          }),
      }),
    end_time: yup
      .mixed()
      .transform((val) => yupMixedTimeToWallHHmm(val))
      .when("is_all_day", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .required("Select end date and time.")
            .test(
              "is-after-start",
              "End time must come after start time.",
              function (end_time) {
                const { start_time, date_from, date_to } = this.parent;
                if (!moment(date_from).isSame(moment(date_to))) return true;

                const wallHmMoment = (hhmm) => {
                  const m = moment(hhmm, "HH:mm", true);
                  return m.isValid()
                    ? m.clone().set({ year: 2000, month: 1, date: 1 })
                    : moment.invalid();
                };
                const start = wallHmMoment(start_time);
                const end = wallHmMoment(end_time);

                return end.isValid() && start.isValid() && end.isAfter(start);
              },
            ),
      }),
    date_to: yup
      .string()
      .test(
        "is-after-start",
        "End date cannot be earlier than start date",
        function (val) {
          const date_from = this.parent.date_from;
          return moment(val).isSameOrAfter(moment(date_from));
        }
      ),
    // GMA-1228: Repeat end date is required when the activity is recurring.
    // The hidden +365d default previously created year-long recurrences
    // whenever a caregiver picked Daily/Weekly without spotting the "Ends"
    // input, which flooded the web calendar.
    repeat_end_date: yup
      .string()
      .nullable()
      .when("repeat_type", ([repeat_type], schema) => {
        if (repeat_type && repeat_type !== "none") {
          return schema
            // `.test` (not `.required`) so empty-string reliably fails in
            // yup v1 regardless of `.nullable()` interaction.
            .test(
              "repeat-end-not-empty",
              "Pick a repeat end date.",
              (val) => typeof val === "string" && val.trim() !== "",
            )
            .test(
              "repeat-end-not-before-start",
              "Repeat end date cannot be before the start date.",
              function (val) {
                if (!val) return true;
                const date_from = this.parent.date_from;
                if (!date_from) return true;
                return moment(val).isSameOrAfter(moment(date_from));
              },
            );
        }
        return schema.notRequired();
      }),
    video_url: yup
      .string()
      .nullable()
      .notRequired()
      .test(
        "is-valid-url",
        "Enter a valid video URL (http or https).",
        function (val) {
          if (!val || val.trim() === "") return true;
          try {
            const parsed = new URL(val.trim());
            return parsed.protocol === "http:" || parsed.protocol === "https:";
          } catch {
            return false;
          }
        }
      )
      .max(2048, "Video URL must be 2048 characters or fewer."),
    steps: yup.array().of(
      yup.object({
        title: yup
          .string()
          .required("Enter step name.")
          .max(100, "Step names can't be over 100 characters."),
      }),
    ),
  });

export const useActivityForm = () => {
  const { selectedClient } = useContext(ClientContext);
  const {
    selectedActivity,
    createActivity,
    updateActivity,
    deleteActivityImage,
    deleteStepImage,
  } = useActivities();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [pendingStepImageDeletes, setPendingStepImageDeletes] = useState([]);
  const [pendingActivityImageDelete, setPendingActivityImageDelete] =
    useState(false);

  const navEvent = location.state?.event;
  const editingActivity = selectedActivity || navEvent || null;
  const [editingActivityId, setEditingActivityId] = useState(
    editingActivity?.id ?? null,
  );

  const startTime =
    toWallClockHHmm(location.state?.selectedTime) ||
    (typeof location.state?.selectedTime === "string"
      ? location.state.selectedTime.trim()
      : "");
  const endTime = startTime
    ? moment(startTime, "HH:mm").add(1, "hour").format("HH:mm")
    : "";

  // GMA-1228: Track whether the caregiver has manually edited `repeat_end_date`
  // so that subsequent `date_to` changes do not clobber their override. Reset
  // when recurrence is turned off or when a new activity is loaded.
  const repeatEndUserOverrideRef = useRef(false);
  const repeatEndSeededBreadcrumbRef = useRef(false);
  const repeatEndOverrideBreadcrumbRef = useRef(false);
  const repeatEndHydratingProtectedValueRef = useRef(false);

  const activityClientIanaZone = useMemo(() => {
    const { zone } = resolveActivityTimezone(selectedClient?.timezone, {
      source: "activity-form-client-zone",
      clientId: selectedClient?.id ?? null,
    });
    return zone;
  }, [selectedClient?.timezone, selectedClient?.id]);

  const clientMinWallDate = useMemo(() => {
    return moment.tz(activityClientIanaZone).format("YYYY-MM-DD");
  }, [activityClientIanaZone]);

  const validationSchema = useMemo(
    () =>
      buildValidationSchema({
        isEditingActivity: Boolean(editingActivityId),
        rawClientTimezone: selectedClient?.timezone,
        clientId: selectedClient?.id ?? null,
      }),
    [editingActivityId, selectedClient?.timezone, selectedClient?.id],
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      start_time: startTime,
      end_time: endTime,
      date_from: location.state?.selectedDate || "",
      date_to: location.state?.selectedDate || "",
      repeat_type: "none",
      repeat_days: [],
      steps: [],
      activity_image: null,
      video_url: null,
      // GMA-1228: previously defaulted to `moment().add(365, "days")` which
      // silently committed caregivers to year-long recurrences. Empty until
      // the caregiver picks a repeat type; then auto-seeded from `date_to`.
      repeat_end_date: "",
      reminder_enabled: false,
      is_important: false,
      // GMA-1234: when true the form hides the time pickers and the
      // payload builder synthesises 00:00 → next-day 00:00 (client zone)
      // ISO instants from date_from / date_to. See ActivityContext payload.
      is_all_day: false,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (_) => {
      setShowLoadingModal(true);

      try {
        const submittedVideoUrl =
          typeof formik.values.video_url === "string"
            ? formik.values.video_url.trim()
            : "";
        if (submittedVideoUrl) {
          const iosSupport = isIOSSupportedYouTubeUrl(submittedVideoUrl);
          addBreadcrumb("activity.video_url.submit", {
            category: "activity.edit",
            level: "info",
            data: {
              iosSupported: iosSupport,
              host: (() => {
                try {
                  return new URL(submittedVideoUrl).hostname;
                } catch {
                  return "unparseable";
                }
              })(),
            },
          });
          if (iosSupport === false) {
            captureMessage(
              "[GMA-1060] unsupported-shape video URL saved (non-blocking)",
              {
                tags: { ticket: "GMA-1060", action: "video-url-shape-warn" },
                level: "info",
              }
            );
          }
        }

        if (editingActivityId) {
          if (pendingActivityImageDelete) {
            await deleteActivityImage();
          }
          for (const stepId of pendingStepImageDeletes) {
            await deleteStepImage(stepId);
          }
        }

        const submitData = {
          ...formik.values,
        };

        if (formik.values.repeat_type && formik.values.repeat_type !== "none") {
          submitData.repeat_end_date = formik.values.repeat_end_date;
        } else {
          delete submitData.repeat_end_date;
        }

        if (editingActivityId) {
          await updateActivity(editingActivityId, submitData);
        } else {
          await createActivity(submitData);
        }

        setPendingStepImageDeletes([]);
        setPendingActivityImageDelete(false);

        toast.success("Activity saved successfully!");
        navigate(`/calendar/${selectedClient?.id}`, {
          state: { isCaregiver: true },
        });
      } catch (error) {
        formik.setStatus({ error: error.message });
      } finally {
        setShowLoadingModal(false);
      }
    },
  });

  const initData = (existingEvent) => {
    // GMA-1228: resolve the persisted ISO in the client's wall-clock time so
    // the form shows what the caregiver would see on the calendar grid (same
    // source of truth as `formatActivities` / `activityInstantToEventCalendarDate`).
    // Plain `moment(iso).format("YYYY-MM-DD")` used to render the caregiver's
    // browser-local date, which in turn caused save round-trips to re-skew
    // the stored instant.
    const clientZone = selectedClient?.timezone;
    const clientId = selectedClient?.id ?? null;
    const {
      date: dateFrom,
    } = extractZonedFormFields(existingEvent.start_time, clientZone, {
      source: "edit-load",
      clientId,
    });
    let {
      date: dateTo,
    } = extractZonedFormFields(existingEvent.end_time, clientZone, {
      source: "edit-load",
      clientId,
    });
    // GMA-1234 (review feedback): all-day activities persist their end as
    // midnight on the day AFTER `date_to` (FullCalendar exclusive-end
    // convention — see `buildAllDayInstants`). If we hydrate `date_to`
    // straight from `end_time`'s wall date, the form shows the displayed
    // end one day too late and every Save without the user touching the
    // dates would PATCH back end+1, drifting the activity forward by a
    // day per edit. Roll the extracted date back by one day in the
    // client's wall calendar so the form round-trips cleanly.
    //
    // PR #139 re-review (Copilot): route the zone through
    // `resolveActivityTimezone` instead of `clientZone || moment.tz.guess()`
    // so a missing or invalid client.timezone emits the same
    // `activity_timezone_resolve` / `activity_timezone_fallback`
    // breadcrumb the rest of the form (and the writer path) emits. Keying
    // the breadcrumb with `source: "edit-load-all-day-rollback"` lets us
    // tell this fallback apart from the read- and write-side fallbacks.
    if (existingEvent.is_all_day && dateTo) {
      const { zone: rollbackZone } = resolveActivityTimezone(clientZone, {
        source: "edit-load-all-day-rollback",
        clientId,
      });
      const rolledBack = moment
        .tz(dateTo, "YYYY-MM-DD", rollbackZone)
        .subtract(1, "day")
        .format("YYYY-MM-DD");
      dateTo = rolledBack;
    }
    const persistedRepeatType = existingEvent.repeat_type || "none";

    // GMA-1228: If an existing recurring row was persisted with a stretched
    // single-occurrence span (end_time > 24h after start_time), the row was
    // created by the pre-fix +365d default bug. Collapse the form back to a
    // same-day activity so the caregiver saves the corrected shape on the
    // next PATCH, and emit a Sentry breadcrumb so we can watch the one-time
    // backfill settle on stg2 / prod. The 24h check is zone-invariant (delta
    // of two absolute instants), so we can still use plain `moment()` here.
    if (persistedRepeatType !== "none") {
      // GMA-1228-OK below: absolute-instant delta (hours between two ISO
      // instants) is zone-invariant, so bare moment() is acceptable here.
      const start = moment(existingEvent.start_time); // GMA-1228-OK: abs-instant
      const end = moment(existingEvent.end_time); // GMA-1228-OK: abs-instant
      if (end.diff(start, "hours", true) > 24) {
        const originalDateTo = extractZonedFormFields(
          existingEvent.end_time,
          clientZone,
          { source: "edit-load-collapse", clientId },
        ).date;
        dateTo = dateFrom;
        addBreadcrumb("activity.edit.auto_collapsed_span", {
          category: "activity.edit",
          level: "info",
          data: {
            ticket: "GMA-1228",
            activityId: existingEvent.id ?? null,
            originalDateTo,
          },
        });
      }
    }

    // Preserve persisted recurrence windows on edit load. The auto-sync effect
    // may seed empty repeat ends from `date_to`, but it must not truncate a
    // real server value back to the single occurrence end date.
    const hasPersistedRecurringRepeatEnd = Boolean(
      persistedRepeatType !== "none" && existingEvent.repeat_end_date,
    );
    repeatEndUserOverrideRef.current = hasPersistedRecurringRepeatEnd;
    repeatEndHydratingProtectedValueRef.current = hasPersistedRecurringRepeatEnd;
    repeatEndSeededBreadcrumbRef.current = false;
    repeatEndOverrideBreadcrumbRef.current = false;

    // GMA-1228: pull the `HH:mm` wall time from the client zone, not the
    // caregiver's browser zone, so the form renders the same hour the grid
    // shows. `extractZonedFormFields` emits the same resolve / fallback
    // breadcrumb as the write path so both sides of the round-trip stay
    // observable in Sentry.
    const { time: startTimeWall } = extractZonedFormFields(
      existingEvent.start_time,
      clientZone,
      { source: "edit-load", clientId },
    );
    const { time: endTimeWall } = extractZonedFormFields(
      existingEvent.end_time,
      clientZone,
      { source: "edit-load", clientId },
    );

    formik.setValues({
      title: existingEvent.title,
      description: existingEvent.description,
      start_time: startTimeWall,
      end_time: endTimeWall,
      date_from: dateFrom,
      date_to: dateTo,
      is_important: existingEvent.is_important ?? false,
      reminder_enabled: existingEvent.reminder_enabled ?? false,
      repeat_days: existingEvent.repeat_days,
      repeat_type: persistedRepeatType,
      // GMA-1228: previously fell back to `moment().add(365, "days")` when
      // the persisted value was null. Editing a non-recurring row and
      // flipping to Daily/Weekly therefore silently stretched to a year.
      // Now we preserve the server value verbatim (including null/empty)
      // so the correlation effect can seed it from `date_to` on recurrence.
      repeat_end_date: existingEvent.repeat_end_date
        ? moment(existingEvent.repeat_end_date).format("YYYY-MM-DD")
        : "",
      steps: existingEvent.steps || [],
      activity_image: existingEvent.activity_image,
      video_url: existingEvent.video_url || null,
      is_all_day: existingEvent.is_all_day ?? false,
    });
  };

  useEffect(() => {
    if (selectedActivity) {
      initData(selectedActivity);
      setEditingActivityId(selectedActivity.id);
    } else if (navEvent) {
      initData(navEvent);
      setEditingActivityId(navEvent.id);
    }
  }, [selectedActivity, navEvent]);

  /**
   * GMA-1228: Correlate the hidden `repeat_end_date` with the visible
   * `date_to` (Activity ends) field.
   *
   * Before the fix, `repeat_end_date` was seeded with a +365d hardcoded
   * default that the caregiver never saw, producing year-long recurrences
   * and flooding the calendar. Now:
   *
   *   - When the caregiver switches `repeat_type` from "none" to a
   *     recurring value, seed `repeat_end_date` from the current
   *     `date_to` (the value they just typed into the visible field).
   *   - While recurring, keep the two in sync as they update `date_to` —
   *     unless they have manually typed into `repeat_end_date`, in which
   *     case their override wins (tracked via `repeatEndUserOverrideRef`).
   *   - When the caregiver switches back to "none", clear
   *     `repeat_end_date` and the override flag so the next recurring
   *     switch starts from a clean state.
   */
  const { repeat_type, date_to, repeat_end_date } = formik.values;
  useEffect(() => {
    if (!repeat_type || repeat_type === "none") {
      if (repeatEndHydratingProtectedValueRef.current) return;
      if (repeat_end_date) {
        formik.setFieldValue("repeat_end_date", "");
      }
      repeatEndUserOverrideRef.current = false;
      repeatEndSeededBreadcrumbRef.current = false;
      repeatEndOverrideBreadcrumbRef.current = false;
      return;
    }
    repeatEndHydratingProtectedValueRef.current = false;
    if (repeatEndUserOverrideRef.current) return;
    if (!date_to) return;
    if (repeat_end_date === date_to) return;

    formik.setFieldValue("repeat_end_date", date_to);
    if (!repeatEndSeededBreadcrumbRef.current) {
      repeatEndSeededBreadcrumbRef.current = true;
      addBreadcrumb("activity.repeat_end.seeded_from_date_to", {
        category: "activity.edit",
        level: "info",
        data: { ticket: "GMA-1228", date_to, repeat_type },
      });
    }
    // Intentionally omit `repeat_end_date` from deps — we read it to avoid
    // looping, but we only want to react to repeat_type / date_to changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeat_type, date_to]);

  /**
   * GMA-1228: Mark the override flag when the caregiver manually edits the
   * "Repeats until" input. One-shot Sentry breadcrumb so we can observe
   * how often the default seeding is good enough vs overridden.
   */
  const markRepeatEndUserOverride = (newValue) => {
    repeatEndUserOverrideRef.current = true;
    formik.setFieldValue("repeat_end_date", newValue);
    if (!repeatEndOverrideBreadcrumbRef.current) {
      repeatEndOverrideBreadcrumbRef.current = true;
      addBreadcrumb("activity.repeat_end.user_override", {
        category: "activity.edit",
        level: "info",
        data: {
          ticket: "GMA-1228",
          repeat_type: formik.values.repeat_type,
          previous_value: formik.values.repeat_end_date,
          new_value: newValue,
        },
      });
    }
  };

  const handleStepChange = (index, key, value) => {
    const updatedSteps = [...formik.values.steps];
    updatedSteps[index] = { ...updatedSteps[index], [key]: value };
    formik.setFieldValue("steps", updatedSteps);
  };

  const addStep = async () => {
    const sequence = formik.values.steps.length + 1;
    formik.validateForm();
    if (
      sequence > 1 &&
      !formik.values.steps[formik.values.steps.length - 1]?.title
    ) {
      return;
    }
    const updatedSteps = formik.values.steps;
    updatedSteps.push({ title: "", content: "", sequence });
    formik.setFieldValue("steps", updatedSteps);
  };

  const handleStepImageChange = (index, file) => {
    if (file && file.size <= 1000 * 1024 * 1024) {
      const updatedSteps = [...formik.values.steps];
      updatedSteps[index] = {
        ...updatedSteps[index],
        step_image: file,
      };
      formik.setFieldValue("steps", updatedSteps);
    } else {
      alert("Please choose a file under 1GB.");
    }
  };

  const handleStepImageDelete = (index) => {
    const step = formik.values.steps[index];
    if (step.id) {
      setPendingStepImageDeletes((prev) => [...prev, step.id]);
    }
    const updatedSteps = [...formik.values.steps];
    updatedSteps[index] = {
      ...updatedSteps[index],
      step_image: null,
    };
    formik.setFieldValue("steps", updatedSteps);
  };

  const handleDeleteStep = (index) => {
    const updatedSteps = formik.values.steps.filter((_, i) => i !== index);
    formik.setFieldValue("steps", updatedSteps);
  };

  const handleActivityImageChange = (file) => {
    if (file && file.size <= 1000 * 1024 * 1024) {
      formik.setFieldValue("activity_image", file);
    } else {
      alert("Please choose a file under 1GB.");
    }
  };

  const handleActivityImageDelete = () => {
    setPendingActivityImageDelete(true);
    formik.setFieldValue("activity_image", null);
  };

  /**
   * GMA-1060: non-blocking video URL warning.
   *
   * GMA-1053 previously hard-blocked save when a YouTube URL did not match the
   * iOS in-app playback pattern (watch / embed / youtu.be). Caregivers reported
   * this as the "Video URL PLE error" — the expanding helperText pushed the
   * form such that the Save button was unreachable without scrolling, making
   * the field feel like it "disabled scrolling". The iOS client already falls
   * back to a system-handler open when it cannot embed a URL, so hard-blocking
   * on the web portal was over-restrictive.
   *
   * New contract: submit is NOT blocked for unsupported YouTube shapes, but we
   * emit a Sentry breadcrumb + warning surface so the iOS team still has
   * telemetry. Consumers read `videoUrlWarning` to render a non-error hint
   * under the field.
   */
  const rawVideoUrl = formik.values.video_url;
  const trimmedVideoUrl =
    typeof rawVideoUrl === "string" ? rawVideoUrl.trim() : "";
  let videoUrlWarning = "";
  if (trimmedVideoUrl) {
    const iosSupport = isIOSSupportedYouTubeUrl(trimmedVideoUrl);
    if (iosSupport === false) {
      videoUrlWarning =
        "Heads up: this YouTube format may not play inside the mobile app and will open in the user's YouTube / browser instead. Prefer a watch, embed, or youtu.be link for in-app playback.";
    }
  }

  return {
    formik,
    editingActivity,
    editingActivityId,
    handleStepChange,
    addStep,
    handleStepImageChange,
    handleStepImageDelete,
    handleDeleteStep,
    handleActivityImageChange,
    handleActivityImageDelete,
    showLoadingModal,
    videoUrlWarning,
    markRepeatEndUserOverride,
    clientMinWallDate,
    activityClientIanaZone,
  };
};
